import {
    ActivityScheduleRule,
    CommandResponse,
    EntityCommandCreateResponse,
    RevisionedCommandResponse,
    StudyEntity,
    StudyEntityModification,
    StudyEntityType,
    toActivityScheduleRule,
} from '@faro/study-designer-model';
import axios from 'axios';
import { createStudyEntityHookFactory } from '../createStudyEntityHookFactory';
import { pending } from '../../package/optimistic-udpates';
import { ScheduleOfActivitiesQueryParameter, StudyPathParameter, VariantQueryParameter } from '../parameter';
import { isEqualActivityScheduleRule } from '../../component/ActivityScheduleCompositeRuleDialog/utils/isEqualActivityScheduleRule';

const factory = createStudyEntityHookFactory<ActivityScheduleRule>(StudyEntityType.ActivityScheduleRule);

type ActivityScheduleRulePathParameters = StudyPathParameter;
type ActivityScheduleRuleQueryParameters = ScheduleOfActivitiesQueryParameter & VariantQueryParameter;

export const useActivityScheduleRules = factory.list(
    (
        { studyId, ...query }: ActivityScheduleRulePathParameters & ActivityScheduleRuleQueryParameters,
        signal?: AbortSignal
    ): Promise<ActivityScheduleRule[]> =>
        axios
            .get(`/api/study-designer/studies/${studyId}/activity-schedule-rules`, { signal, params: query })
            .then(response => response.data?.map(toActivityScheduleRule) ?? [])
);

export const useInsertActivityScheduleRules = factory.mutate(
    (
        { studyId, ...entityReference }: StudyEntityModification<ActivityScheduleRule>,
        signal?: AbortSignal
    ): Promise<RevisionedCommandResponse<EntityCommandCreateResponse>> =>
        axios
            .post(
                `/api/study-designer/studies/${studyId}/activity-schedule-rules/actions/create-batch`,
                entityReference,
                { signal }
            )
            .then(response => response.data),
    {
        optimisticUpdate: (modifications, cachedData: ActivityScheduleRule[]): ActivityScheduleRule[] => {
            return [
                ...cachedData,
                ...modifications.entities.reduce((pendingRules, rule) => {
                    const exists = cachedData.some(x => isEqualActivityScheduleRule(x, rule));
                    if (!exists) {
                        pendingRules.push(pending(rule));
                    }
                    return pendingRules;
                }, [] as ActivityScheduleRule[]),
            ];
        },
    }
);

export const useUpsertActivityScheduleRules = factory.mutate(
    (
        { studyId, ...entityReference }: StudyEntityModification<ActivityScheduleRule>,
        signal?: AbortSignal
    ): Promise<RevisionedCommandResponse<EntityCommandCreateResponse>> =>
        axios
            .post(
                `/api/study-designer/studies/${studyId}/activity-schedule-rules/actions/save-batch`,
                entityReference,
                { signal }
            )
            .then(response => response.data)
);

export const useUpdateActivityScheduleRules = factory.mutate(
    (entity: ActivityScheduleRule, signal?: AbortSignal): Promise<RevisionedCommandResponse<CommandResponse>> =>
        axios
            .put(`/api/study-designer/studies/${entity.studyId}/activity-schedule-rules/${entity.id}`, entity, {
                signal,
            })
            .then(response => response.data),
    { optimisticUpdate: (rule, cachedData) => cachedData.map(x => (x.id === rule.id ? rule : x)) }
);

export const useDeleteActivityScheduleRule = factory.mutate(
    (
        entity: Pick<StudyEntity, 'id' | 'studyId'>,
        signal?: AbortSignal
    ): Promise<RevisionedCommandResponse<CommandResponse>> =>
        axios
            .delete(`/api/study-designer/studies/${entity.studyId}/activity-schedule-rules/${entity.id}`, { signal })
            .then(response => response.data),
    { optimisticUpdate: (rule, cachedData) => cachedData.filter(x => x.id !== rule.id) }
);
